import { ReactElement, ReactNode, useEffect, useState } from 'react';

import SignalRSOS from '~/ui/pages/Notifications/components/SignalRSOS';

import useTabVisibility from '~/hooks/browser/useTabVisibility';
import useIsOnline from '~/hooks/network/useIsOnline';
import { getAccessToken } from '~/services/localStorage/auth';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import isDev from '~/utils/isDev';

interface IProps {
  children: ReactNode | ReactNode[];
}
let tries = 0;

const PanicRequestsProvider = ({ children }: IProps): ReactElement => {
  const { isSuperAdmin, isProductOwner } = useRole();
  const isTabActive = useTabVisibility();

  const isConnectionDisabled = isSuperAdmin || isProductOwner || !isTabActive;

  const { current } = useStoreState(state => state.user);
  const { onGetCurrentProfile } = useStoreActions(state => state.user);
  const isOnline = useIsOnline();

  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = getAccessToken();

    if (accessToken && !token && current) {
      setToken(accessToken);
    } else if (!accessToken) {
      setToken(null);
    }
  }, [token, current]);

  useEffect(() => {
    if (isOnline) {
      tries = 0;
      setToken(null);
    }
  }, [isOnline]);

  const connectionCloseDelay = () =>
    new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });

  // eslint-disable-next-line consistent-return
  const handleError = async (error?: Error) => {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('error: ', JSON.stringify(error));
    }
    if (tries >= 5) {
      return connectionCloseDelay();
    }
    // Trigger Axios interceptor to refresh the token upon encountering an error.
    // The error can stem from various issues, such as a loss of internet connectivity, server unreachability, or a SignalR service outage.
    // A maximum of 5 retry attempts will be made. The retry counter will be reset upon a page refresh or restoration of network connectivity.
    // If the token refresh process fails, the user will be logged out by the Axios interceptor.
    await onGetCurrentProfile();
    tries += 1;
    setToken(null);
  };

  const handleConnection = () => {
    tries = 0;
  };

  const isConnected = !!token && !isConnectionDisabled && isOnline;

  return (
    <SignalRSOS.SignalRContext.Provider
      connectEnabled={isConnected}
      withCredentials={false}
      dependencies={[token]}
      transport={2}
      url={process.env.REACT_APP_API_URL.replace(
        'api/v1/',
        `hubs/panic-request?access_token=${token}`,
      )}
      logger={6}
      onBeforeClose={connectionCloseDelay}
      onOpen={handleConnection}
      onReconnect={handleConnection}
      onError={handleError}
    >
      {children}
    </SignalRSOS.SignalRContext.Provider>
  );
};
export default PanicRequestsProvider;
